import { useCallback, useMemo, useRef } from 'react';
import { ApolloCache } from '@apollo/client';

import {
  GroupSmsCampaignsDocument,
  GroupSmsCampaignsQuery,
  GroupSmsCampaignsQueryVariables,
  GroupSmsCandidatesQuery,
  GroupSmsPreviewQuery,
  SmsCampaignFieldsFragment,
  useGroupSmsCampaignsQuery,
  useGroupSmsCandidatesQuery,
  useGroupSmsPreviewQuery,
  useLaunchGroupSmsMutation,
  useRepeatSmsCampaignMutation,
  useSmsCampaignProgressQuery,
} from 'core/graphql';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { ISmsError, smsCrashErrors, toSmsErrors } from './smsFeedback';
import {
  ISmsLinkDraft,
  SMS_CAMPAIGN_POLL_MS,
  SmsCampaignMode,
  smsLinkInput,
} from './smsModel';
import { useSmsPermissions } from './useSmsPermissions';
import { useSmsPreviewInput } from './useSmsPreviewInput';

/**
 * ТЗ 0081 — рассылка SMS по группе: кому уйдёт (и почему кому-то нет),
 * предпросмотр на первом получателе, запуск, история, опрос итогов и
 * «Повторить упавшим».
 */

export type ISmsCampaign = SmsCampaignFieldsFragment;
export type ISmsCampaignCandidate = GroupSmsCandidatesQuery['smsCampaignCandidates'][number];
export type ISmsCampaignPreview = NonNullable<
  GroupSmsPreviewQuery['previewSmsCampaign']
>;

const EMPTY_CANDIDATES: ISmsCampaignCandidate[] = [];
const EMPTY_CAMPAIGNS: ISmsCampaign[] = [];

export const useGroupSmsCandidates = (
  groupId: string,
  mode: SmsCampaignMode,
  enabled = true,
) => {
  const { canUse } = useSmsPermissions();
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useGroupSmsCandidatesQuery({
    variables: { groupId, mode },
    skip: !canUse || !enabled || !groupId,
    fetchPolicy: 'cache-and-network',
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  const current = data ?? previousData;
  return {
    candidates: current?.smsCampaignCandidates ?? EMPTY_CANDIDATES,
    loading: loading && !current,
    refreshing: loading,
    /**
     * Строки ЭТОГО режима пришли и не перечитываются. Пока едет новый
     * режим, `candidates` — строки прошлого (`previousData`): засевать по
     * ним выбор получателей нельзя.
     */
    settled: Boolean(data) && !loading,
    failed: Boolean(error) && !current,
    reload,
  };
};

/**
 * Выбор получателей в переменных: отсортирован, чтобы снять и вернуть
 * флажок не было новым запросом; `null` — выбора нет (все кандидаты).
 */
const useStableIds = (ids: readonly string[] | null | undefined) => {
  const key = ids ? JSON.stringify([...ids].sort()) : '';
  return useMemo<string[] | null>(
    () => (key ? (JSON.parse(key) as string[]) : null),
    [key],
  );
};

/**
 * Предпросмотр рассылки: образец на первом получателе и счёт. `studentIds`
 * — выбор сотрудника: счёт и образец — только по выбранным.
 */
export const useGroupSmsPreview = (
  groupId: string,
  text: string,
  link: ISmsLinkDraft,
  mode: SmsCampaignMode,
  enabled = true,
  studentIds: readonly string[] | null = null,
) => {
  const { canUse } = useSmsPermissions();
  const { debounced, lagging, hasText } = useSmsPreviewInput(text, link);
  const ids = useStableIds(studentIds);
  const skip = !canUse || !enabled || !groupId || !debounced.text;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useGroupSmsPreviewQuery({
    variables: {
      groupId,
      text: debounced.text,
      link: debounced.link,
      mode,
      studentIds: ids,
    },
    skip,
    fetchPolicy: 'network-only',
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  if (!hasText) {
    return { preview: null, pending: false, failed: false, reload };
  }
  const current = (data ?? previousData)?.previewSmsCampaign ?? null;
  return {
    preview: current,
    pending: lagging || loading,
    failed: Boolean(error) && !current,
    reload,
  };
};

export const useGroupSmsCampaigns = (groupId: string, enabled = true) => {
  const { canUse } = useSmsPermissions();
  const { data, loading, error, refetch } = useGroupSmsCampaignsQuery({
    variables: { groupId },
    skip: !canUse || !enabled || !groupId,
    fetchPolicy: 'cache-and-network',
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  return {
    campaigns: data?.smsCampaigns ?? EMPTY_CAMPAIGNS,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
  };
};

/**
 * Опрос одной рассылки, пока она не закончилась. Возвращает свежий итог —
 * его рисует строка истории САМА, не дожидаясь списка.
 *
 * 🔴 Не полагаться на то, что запись опроса в `SmsCampaignType:<id>`
 * перерисует список: в Apollo 3.3.6 у запроса истории залипает
 * `QueryInfo.dirty` (ответ соседнего запроса пришёл, пока история была в
 * пути), и чужая запись в ту же сущность больше не будит его — строка
 * навсегда оставалась «идёт», а опрос не выключался (дымовой тест
 * `smsSurfaces.smoke`). Ответ собственного запроса доходит всегда.
 */
export const useSmsCampaignPoll = (
  campaignId: string,
  finished: boolean,
): ISmsCampaign | null => {
  const { canUse } = useSmsPermissions();
  const skip = !canUse || !campaignId || finished;
  const { data, startPolling, stopPolling } = useSmsCampaignProgressQuery({
    variables: { id: campaignId },
    skip,
    fetchPolicy: 'network-only',
  });
  usePageVisiblePoll(startPolling, stopPolling, SMS_CAMPAIGN_POLL_MS, !skip);
  return data?.smsCampaign ?? null;
};

export interface IGroupSmsSendInput {
  title: string;
  text: string;
  link: ISmsLinkDraft;
  mode: SmsCampaignMode;
  templateId: string | null;
  requestKey: string;
  /** Кому из кандидатов: выбор сотрудника; `null` — всем кандидатам режима. */
  studentIds: readonly string[] | null;
}

export interface IGroupSmsSendResult {
  ok: boolean;
  errors: ISmsError[];
  campaign: ISmsCampaign | null;
}

/** Новая рассылка встаёт в начало истории группы — без перечитывания. */
const prependCampaign = (
  groupId: string,
  campaign: ISmsCampaign,
  cache: ApolloCache<unknown>,
) => {
  const variables = { groupId };
  const current = cache.readQuery<
    GroupSmsCampaignsQuery,
    GroupSmsCampaignsQueryVariables
  >({ query: GroupSmsCampaignsDocument, variables });
  if (!current) return;
  if (current.smsCampaigns.some((item) => item.id === campaign.id)) return;
  cache.writeQuery<GroupSmsCampaignsQuery, GroupSmsCampaignsQueryVariables>({
    query: GroupSmsCampaignsDocument,
    variables,
    data: { smsCampaigns: [campaign, ...current.smsCampaigns] },
  });
};

export const useGroupSmsActions = (groupId: string) => {
  const [launchMut, { loading: sending }] = useLaunchGroupSmsMutation();
  const [repeatMut, { loading: resending }] = useRepeatSmsCampaignMutation();
  const inFlight = useRef(false);

  const send = useCallback(
    async (input: IGroupSmsSendInput): Promise<IGroupSmsSendResult> => {
      if (inFlight.current) return { ok: false, errors: [], campaign: null };
      inFlight.current = true;
      try {
        const { data } = await launchMut({
          variables: {
            groupId,
            text: input.text.trim(),
            title: input.title.trim() || null,
            link: smsLinkInput(input.link),
            mode: input.mode,
            templateId: input.templateId || null,
            requestKey: input.requestKey,
            studentIds: input.studentIds ? [...input.studentIds] : null,
          },
          update: (cache, { data: answer }) => {
            const campaign = answer?.sendGroupSms?.campaign;
            if (campaign) prependCampaign(groupId, campaign, cache);
          },
        });
        const payload = data?.sendGroupSms;
        const errors = toSmsErrors(payload?.customErrors);
        return {
          ok: Boolean(payload?.success) && !errors.length,
          errors,
          campaign: payload?.campaign ?? null,
        };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught), campaign: null };
      } finally {
        inFlight.current = false;
      }
    },
    [groupId, launchMut],
  );

  const resend = useCallback(
    async (
      campaignId: string,
    ): Promise<{
      ok: boolean;
      errors: ISmsError[];
      created: number;
      campaign: ISmsCampaign | null;
    }> => {
      if (inFlight.current) {
        return { ok: false, errors: [], created: 0, campaign: null };
      }
      inFlight.current = true;
      try {
        const { data } = await repeatMut({ variables: { campaignId } });
        const payload = data?.resendSmsCampaign;
        const errors = toSmsErrors(payload?.customErrors);
        return {
          ok: Boolean(payload?.success) && !errors.length,
          errors,
          created: payload?.created ?? 0,
          campaign: payload?.campaign ?? null,
        };
      } catch (caught) {
        return {
          ok: false,
          errors: smsCrashErrors(caught),
          created: 0,
          campaign: null,
        };
      } finally {
        inFlight.current = false;
      }
    },
    [repeatMut],
  );

  return { send, resend, busy: { sending, resending } };
};
